import React from "react";
import injectSheet from "react-jss";
import { Colors } from "../config";
import { getLinkStatus } from "./studio/sceneStatus";

const MINIMAP_SIZE_DEFAULT = 150;
const PAD = 8;
const GRID_STEPS = 6;

const NODE_COLORS = {
  linked: Colors.Green,
  unlinked: Colors.Orange,
  active: Colors.Blue,
};

class TourCanvasMinimap extends React.Component {
  constructor(props) {
    super(props);
    this.state = { size: MINIMAP_SIZE_DEFAULT };
    this.onClick = this.onClick.bind(this);
  }

  componentDidMount() {
    this.observeSize();
    this.scheduleDraw();
  }

  componentDidUpdate() {
    this.scheduleDraw();
  }

  setContainerRef = ref => {
    this.container = ref;
    if (ref) {
      this.observeSize();
      this.updateSize();
    }
  };

  setCanvasRef = ref => {
    this.canvas = ref;
    if (ref) {
      this.scheduleDraw();
    }
  };

  scheduleDraw() {
    if (this.drawFrame) {
      cancelAnimationFrame(this.drawFrame);
    }
    this.drawFrame = requestAnimationFrame(() => this.draw());
  }

  componentWillUnmount() {
    if (this.drawFrame) {
      cancelAnimationFrame(this.drawFrame);
    }
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
    }
  }

  observeSize() {
    if (this._resizeObserver || !this.container) return;
    if (typeof ResizeObserver === "undefined") return;

    this._resizeObserver = new ResizeObserver(() => this.updateSize());
    this._resizeObserver.observe(this.container);
  }

  updateSize() {
    if (!this.container) return;

    const styles = window.getComputedStyle(this.container);
    const paddingX =
      parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight);
    const width = Math.floor(this.container.clientWidth - paddingX);

    if (width > 0 && width !== this.state.size) {
      this.setState({ size: width }, () => this.scheduleDraw());
    }
  }

  toMinimap(worldX, worldY) {
    const { boardLimit } = this.props;
    const { size } = this.state;
    const inner = size - PAD * 2;
    const x = PAD + ((worldX + boardLimit) / (2 * boardLimit)) * inner;
    const y = PAD + ((boardLimit - worldY) / (2 * boardLimit)) * inner;
    return { x, y };
  }

  toWorld(px, py) {
    const { boardLimit } = this.props;
    const { size } = this.state;
    const inner = size - PAD * 2;
    const worldX = ((px - PAD) / inner) * 2 * boardLimit - boardLimit;
    const worldY = boardLimit - ((py - PAD) / inner) * 2 * boardLimit;
    return { worldX, worldY };
  }

  drawBoardGrid(ctx, tl, br) {
    ctx.strokeStyle = "rgba(149, 149, 149, 0.12)";
    ctx.lineWidth = 1;

    const w = br.x - tl.x;
    const h = br.y - tl.y;

    for (let i = 1; i < GRID_STEPS; i += 1) {
      const t = i / GRID_STEPS;
      const x = tl.x + w * t;
      const y = tl.y + h * t;

      ctx.beginPath();
      ctx.moveTo(x, tl.y);
      ctx.lineTo(x, br.y);
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(tl.x, y);
      ctx.lineTo(br.x, y);
      ctx.stroke();
    }
  }

  drawNode(ctx, x, y, isActive, linkStatus) {
    if (isActive) {
      ctx.beginPath();
      ctx.arc(x, y, 6, 0, 2 * Math.PI);
      ctx.fillStyle = "rgba(55, 187, 200, 0.2)";
      ctx.fill();

      ctx.beginPath();
      ctx.arc(x, y, 4.5, 0, 2 * Math.PI);
      ctx.strokeStyle = NODE_COLORS.active;
      ctx.lineWidth = 1.5;
      ctx.stroke();

      ctx.beginPath();
      ctx.arc(x, y, 2.5, 0, 2 * Math.PI);
      ctx.fillStyle = NODE_COLORS.active;
      ctx.fill();
      return;
    }

    const color =
      linkStatus === "linked" ? NODE_COLORS.linked : NODE_COLORS.unlinked;

    ctx.beginPath();
    ctx.arc(x, y, 3, 0, 2 * Math.PI);
    ctx.fillStyle = color;
    ctx.fill();

    ctx.beginPath();
    ctx.arc(x, y, 3, 0, 2 * Math.PI);
    ctx.strokeStyle = "rgba(0, 0, 0, 0.35)";
    ctx.lineWidth = 0.5;
    ctx.stroke();
  }

  draw() {
    if (!this.canvas) return;

    const { size } = this.state;
    const ctx = this.canvas.getContext("2d");
    ctx.clearRect(0, 0, size, size);

    const { images, activeImage, panX, panY, scale, canvasRect, gridUnit } =
      this.props;

    // Canvas backdrop.
    ctx.fillStyle = Colors.VantaBlack;
    ctx.fillRect(0, 0, size, size);

    const tl = this.toMinimap(-this.props.boardLimit, this.props.boardLimit);
    const br = this.toMinimap(this.props.boardLimit, -this.props.boardLimit);
    const boardW = br.x - tl.x;
    const boardH = br.y - tl.y;

    // Pasteboard fill.
    ctx.fillStyle = "rgba(37, 37, 37, 0.65)";
    ctx.fillRect(tl.x, tl.y, boardW, boardH);

    this.drawBoardGrid(ctx, tl, br);

    // Pasteboard border.
    ctx.strokeStyle = "rgba(149, 149, 149, 0.35)";
    ctx.lineWidth = 1;
    ctx.strokeRect(tl.x + 0.5, tl.y + 0.5, boardW - 1, boardH - 1);

    // Viewport — draw under nodes so dots stay readable on top.
    if (canvasRect && canvasRect.width && scale) {
      const halfW = canvasRect.width / (gridUnit * scale) / 2;
      const halfH = canvasRect.height / (gridUnit * scale) / 2;
      const centerX = -panX;
      const centerY = -panY;

      const vTL = this.toMinimap(centerX - halfW, centerY + halfH);
      const vBR = this.toMinimap(centerX + halfW, centerY - halfH);
      const vw = vBR.x - vTL.x;
      const vh = vBR.y - vTL.y;

      ctx.fillStyle = "rgba(55, 187, 200, 0.1)";
      ctx.fillRect(vTL.x, vTL.y, vw, vh);

      ctx.strokeStyle = Colors.Blue;
      ctx.lineWidth = 1;
      ctx.strokeRect(vTL.x + 0.5, vTL.y + 0.5, vw - 1, vh - 1);
    }

    // Nodes — inactive first, active on top.
    const sorted = [...images].sort((a, b) => {
      if (a.id === activeImage) return 1;
      if (b.id === activeImage) return -1;
      return 0;
    });

    for (const image of sorted) {
      const { x, y } = this.toMinimap(image.x, image.y);
      this.drawNode(
        ctx,
        x,
        y,
        image.id === activeImage,
        getLinkStatus(image),
      );
    }
  }

  onClick(e) {
    const rect = this.canvas.getBoundingClientRect();
    const scaleX = this.state.size / rect.width;
    const scaleY = this.state.size / rect.height;
    const px = (e.clientX - rect.left) * scaleX;
    const py = (e.clientY - rect.top) * scaleY;
    const { worldX, worldY } = this.toWorld(px, py);
    this.props.onJump(worldX, worldY);
  }

  render() {
    const { classes } = this.props;
    const { size } = this.state;

    return (
      <div className={classes.container} ref={this.setContainerRef}>
        <canvas
          className={classes.canvas}
          width={size}
          height={size}
          ref={this.setCanvasRef}
          onClick={this.onClick}
          title="Click to jump to a location"
        />
      </div>
    );
  }
}

const styles = theme => ({
  container: {
    width: "100%",
    boxSizing: "border-box",
    backgroundColor: theme.colorBlack,
    border: `1px solid ${theme.colorDarkGrey}`,
    borderRadius: theme.borderRadius,
    padding: theme.spacing1,
    userSelect: "none",
    overflow: "hidden",
    transitionProperty: "border-color",
    transitionDuration: theme.transitionDuration,
    "&:hover": {
      borderColor: theme.colorGrey,
    },
  },
  canvas: {
    display: "block",
    width: "100%",
    height: "auto",
    aspectRatio: "1",
    cursor: "pointer",
    borderRadius: theme.borderRadius,
    boxShadow: "inset 0 1px 4px rgba(0, 0, 0, 0.45)",
  },
});

export default injectSheet(styles)(TourCanvasMinimap);
